import React from 'react';


import { LegOptionContext } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import {
	useIsNarrowTripCard,
	useBuildLegOptionContext,
} from 'Modules/MultiStopWizard/Components/Leg/Contexts/useLegOptionContext';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import TripOptionMobile from './TripOptionMobile';
import TripOptionWeb from './TripOptionWeb';

interface TripOptionProps {
	trip: TicketsTabTrip;
	price?: number;
	isSelected?: boolean;
	isLoading?: boolean;
	legType: 'departure' | 'return';
	refreshTrips?: () => void;
}

/**
 * A sailing on the ticket-selection step, in the redesigned styling.
 *
 * <p>Replaces <c>LegOption</c>, which is kept beside it for now. The facts come from the same hook
 * the old card used, so the two disagree about nothing: which sailings can be taken, why one cannot,
 * what is left on it and what pressing it does are all decided in
 * <c>useLegOptionContext</c>.</p>
 *
 * <p>Four states, two of which the list never shows. Choosing a sailing hides the day rail and the
 * other sailings, so a selected card is only ever drawn on its own — which is why the selected
 * variants here are the full-width summary and the phone's stacked card, and not a highlighted row
 * among the others.</p>
 */
export default function TripOption({
	trip,
	price,
	isSelected = false,
	isLoading,
	legType,
	refreshTrips,
}: TripOptionProps) {
	const isNarrow = useIsNarrowTripCard();

	const base = useBuildLegOptionContext({ trip, price, isSelected, isLoading, legType, refreshTrips });

	// The redesigned chips name themselves. They sit on a line of their own here, where `Cars 40`
	// reads at a glance and the old card's tooltip is a thing nobody opens — and a capacity that
	// says `(closed)` carries a mark saying what that means, since this is the step where a booker
	// is trying to buy the thing that is closed.
	/*
		A staff chip answers a different question from a customer's.

		Staff and managers can both see a sailing a customer is locked out of, and neither is told
		why: `(closed)` is written for customers and skipped for anyone with `isStaff`, so staff
		read a bare figure with nothing saying the capacity behind it is shut. What they need from
		the chip is its state — the name is already printed on it, and a tooltip repeating that
		spends the only hover the chip has saying nothing.

		On for everyone. A customer reading `Truck ●` has no idea whether that capacity is full or
		shut, and the dot cannot tell them; the tooltip can. The raw figure stays staff-only — that
		gate lives in the tag itself, since `-2` is a number only someone who can overbook can use.
	*/
	const context = React.useMemo(
		() => ({
			...base,
			showCapacityNames: true,
			explainClosedCapacity: true,
			explainCapacityState: true,
		}),
		[base],
	);

	return (
		<LegOptionContext.Provider value={context}>
			{isNarrow ? <TripOptionMobile /> : <TripOptionWeb />}
		</LegOptionContext.Provider>
	);
}
